import axios, { AxiosInstance } from 'axios'
import { HAPTTIC_X_API_KEY } from 'src/constants/env'
import {
  TopicDetail,
  TopicsOverviewData,
  TopicsTableData,
  MentionSentiment,
} from 'src/interfaces/topics.interface'
import { getSessionIdFromLocalStorage } from 'src/localStorage/sessionId/getSessionIdFromLocalStorage/getSessionIdFromLocalStorage'
import { ANALYTICS_API_URL } from 'src/services/constants/urls'

const getHeaders = () => ({
  'Content-Type': 'application/json',
  'X-Api-Key': HAPTTIC_X_API_KEY,
  'x-session-id': getSessionIdFromLocalStorage() || '',
})

export interface TopicsRangeParams {
  fromDate?: string
  toDate?: string
}

// The detail read, narrowed to one subtopic of the topic when set.
export interface TopicDetailParams extends TopicsRangeParams {
  // Narrows relatedMentions alone; the rest still describes the whole topic.
  sentiment?: MentionSentiment
  subtopic?: string
}

interface Envelope<T> {
  data: T
  success: boolean
}

class SocialTopicsApiService {
  private static _instance: SocialTopicsApiService

  private client: AxiosInstance

  private constructor() {
    this.client = axios.create({
      baseURL: ANALYTICS_API_URL,
      headers: getHeaders(),
    })
  }

  private static get instance(): SocialTopicsApiService {
    if (!SocialTopicsApiService._instance) {
      SocialTopicsApiService._instance = new SocialTopicsApiService()
    }
    return SocialTopicsApiService._instance
  }

  public static async getOverview(
    campaignId: string,
    params?: TopicsRangeParams
  ): Promise<TopicsOverviewData> {
    const response = await SocialTopicsApiService.instance.client.get<
      Envelope<TopicsOverviewData>
    >(`/v1_private/campaign/${campaignId}/topics/overview`, {
      headers: getHeaders(),
      params,
    })
    return response.data.data
  }

  /**
   * The overview merged across several campaigns of the account — what the
   * campaigns page shows for the campaigns in view. Ids are sent as one
   * comma-separated `campaigns` param; the backend drops any the user does not
   * own and treats an empty list as "all of them".
   */
  public static async getOverviewForCampaigns(
    campaignIds: string[],
    params?: TopicsRangeParams
  ): Promise<TopicsOverviewData> {
    const response = await SocialTopicsApiService.instance.client.get<
      Envelope<TopicsOverviewData>
    >(`/v1_private/topics/overview`, {
      headers: getHeaders(),
      params: { ...params, campaigns: campaignIds.join(',') },
    })
    return response.data.data
  }

  public static async getTable(
    campaignId: string,
    params?: TopicsRangeParams
  ): Promise<TopicsTableData> {
    const response = await SocialTopicsApiService.instance.client.get<
      Envelope<TopicsTableData>
    >(`/v1_private/campaign/${campaignId}/topics`, {
      headers: getHeaders(),
      params,
    })
    return response.data.data
  }

  public static async getDetail(
    campaignId: string,
    topic: string,
    params?: TopicDetailParams
  ): Promise<TopicDetail> {
    const response = await SocialTopicsApiService.instance.client.get<
      Envelope<TopicDetail>
    >(
      `/v1_private/campaign/${campaignId}/topics/${encodeURIComponent(topic)}`,
      { headers: getHeaders(), params }
    )
    return response.data.data
  }
}

export const socialTopicsApi = SocialTopicsApiService
